import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Chip, Spinner, Typography, Button, Tabs, IconButton, ButtonGroup } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { PiPathFill, PiGear, PiGridFour, PiList } from 'react-icons/pi'
import { AiOutlineEdit, AiOutlinePlus } from 'react-icons/ai'
import { useTranslation } from 'react-i18next'
import { useUserExperienceDetails } from '../hooks/useUserExperienceDetails'
import { useUserAgamiListByExperience } from '../hooks/useUserAgamiListByExperience'
import { usePageTitle, useScreenBreakpoint } from '../hooks/toolkit'
import { useStoredState } from '../hooks/useStoredState'
import { useAgamiVizMode } from '../hooks/useAgamiVizMode'
import { useFeatureFlag } from '../hooks/authorization'
import { EXP_SETTINGS_SIDEBAR_KEY } from '../utils/constants/localStorage.constants'
import { FEATURE_FLAGS } from '../utils/constants/features.constants'
import {
  AgamiMediaFilter,
  AgamiSortMode,
  filterAgamiList,
  groupProposals,
  hasPendingSubmission,
  partitionByDraft,
  sortAgamiList,
} from '../utils/helpers/agami.helpers'
import { useProposalContext } from '../hooks/useProposalContext'
import { useAccessibleExperiences } from '../hooks/useAccessibleExperiences'
import { CAPABILITIES } from '../utils/constants/capabilities.constants'
import { AgamiSpot } from '../types/supabase'

// contexts
import { useDarkMode } from '../contexts/DarkModeContext'

// components
import GoBack from '../components/GoBack'
import { RemoveAgamiFromExperienceDialog } from './RemoveAgamiFromExperienceDialog'
import { RejectProposalDialog } from './RejectProposalDialog'
import { AddAgamiToExperience } from './AddAgamiToExperience'
import { ExperienceSettings } from './ExperienceSettings'
import ResponsiveDialog from '../components/ResponsiveDialog'
import EditExperienceForm from '../components/EditExperienceForm'
import ExperienceAgamiFilters from './ExperienceAgamiFilters'
import ExperienceSpotsSections from './ExperienceSpotsSections'

export const ExperienceDashboard: React.FC = () => {
  const { t, i18n } = useTranslation()
  usePageTitle(t('dashboardExperienceDetail.pageTitle'))
  const { experienceId } = useParams()
  const { darkMode } = useDarkMode()

  const [showDetails, setShowDetails] = useStoredState({
    key: EXP_SETTINGS_SIDEBAR_KEY,
    defaultValue: false,
  })

  const [vizMode, setVizMode] = useAgamiVizMode()

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [agamiToRemove, setAgamiToRemove] = useState<AgamiSpot | null>(null)
  const screenBreakpoint = useScreenBreakpoint()
  const navigate = useNavigate()

  const {
    agamiList,
    isLoading: agamiListLoading,
    refetch: refetchAgamiList,
  } = useUserAgamiListByExperience({ experienceId })

  const {
    experienceDetails,
    isLoading: experienceDetailsLoading,
    refetch: refetchExperienceDetails,
  } = useUserExperienceDetails({ experienceId })

  const startRemove = (agami: AgamiSpot) => {
    setAgamiToRemove(agami)
    setRemoveDialogOpen(true)
  }

  const [typeFilter, setTypeFilter] = useState<number | null>(null)
  const [mediaFilter, setMediaFilter] = useState<AgamiMediaFilter | null>(null)
  const [sortMode, setSortMode] = useState<AgamiSortMode>('default')
  // Filters FIRST, partition AFTER (05.3): the filters apply to both sections
  // and the section counters reflect what is being looked at. The other way
  // round, a filter could empty a section and leave its counter untouched.
  // Client-side on the loaded data — getSpotsByExperienceId stays untouched:
  // its user_id filter is what keeps Agami Global readable.
  const filtered = useMemo(
    () => filterAgamiList(agamiList ?? [], { type: typeFilter, media: mediaFilter }),
    [agamiList, typeFilter, mediaFilter]
  )

  const sorted = useMemo(
    () => sortAgamiList(filtered, sortMode, i18n.resolvedLanguage ?? i18n.language),
    [filtered, sortMode, i18n.resolvedLanguage, i18n.language]
  )

  // ONE partition for both views: two filters in two files would diverge at
  // the first change. With no drafts the page renders exactly as before the
  // sections existed — an empty section is noise, and for almost every
  // experience it is the normal case.
  const { published, drafts } = useMemo(() => partitionByDraft(sorted), [sorted])

  // Derived from the loaded list, like everything else here: no aggregate call.
  const pendingCount = useMemo(() => drafts.filter(hasPendingSubmission).length, [drafts])

  // Duplicate proposals collapse into one entry per normalized title (05.4-T8):
  // twelve "musica" are one row with a counter and ONE rejection.
  const { workingDrafts, proposalGroups } = useMemo(() => groupProposals(drafts), [drafts])

  // Names and the moderation gate in one call, and only when there is a queue:
  // get-proposal-context answers 200 with the proposer names when the caller
  // holds `moderate`, 403 otherwise — so the controls exist if and only if the
  // capability does (AC-6), with no ownership comparison in the client.
  const { canModerate, proposers } = useProposalContext({ experienceId, enabled: pendingCount > 0 })

  // What the Bozze section renders: group representatives first (they are the
  // queue asking for a decision), working drafts after. The representative
  // carries the group on itself — spotIds, counter, who/when — so the two views
  // stay dumb.
  const draftsForView = useMemo(
    () => [
      ...proposalGroups.map(({ representative, meta }) => ({
        ...representative,
        proposal: {
          ...meta,
          proposerName: meta.submittedBy ? proposers[meta.submittedBy] : undefined,
        },
      })),
      ...workingDrafts,
    ],
    [proposalGroups, workingDrafts, proposers]
  )

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [proposalToReject, setProposalToReject] = useState<AgamiSpot | null>(null)

  const startReject = (agami: AgamiSpot) => {
    setProposalToReject(agami)
    setRejectDialogOpen(true)
  }

  // The history link (12.3) rides the CAPABILITY computed server-side (01.7),
  // not `owner_id === user.id`: `view_submissions` belongs to no membership
  // role today, so in practice this means "the owner" — as an authorization,
  // not as a coincidence.
  const { experiences: accessibleExperiences } = useAccessibleExperiences()
  const canViewSubmissions = Boolean(
    accessibleExperiences
      ?.find((exp) => exp.id === experienceId)
      ?.capabilities?.includes(CAPABILITIES.VIEW_SUBMISSIONS)
  )

  const qrCodeFlag = useFeatureFlag(FEATURE_FLAGS.QR_TRIGGER)
  const showQrCode = Boolean(experienceDetails?.qr_trigger && qrCodeFlag)

  const activeSegmentButtonClass = 'border-primary text-primary bg-white-yellow dark:bg-accent-blue-600'

  return (
    <div className="py-4">
      <RemoveAgamiFromExperienceDialog
        isOpen={removeDialogOpen}
        setIsOpen={setRemoveDialogOpen}
        experienceId={experienceId!}
        agamiToRemove={agamiToRemove}
        experienceDetails={experienceDetails}
        refetchAgamiList={refetchAgamiList}
      />

      <RejectProposalDialog
        isOpen={rejectDialogOpen}
        setIsOpen={setRejectDialogOpen}
        proposalToReject={proposalToReject}
        refetchAgamiList={refetchAgamiList}
      />

      {experienceDetailsLoading && <Spinner className="h-6 w-6" />}

      {!experienceDetailsLoading && !experienceDetails && (
        <Typography>{t('dashboardExperienceDetail.noContent')}</Typography>
      )}

      {!experienceDetailsLoading && experienceDetails && (
        <>
          <ResponsiveDialog
            isOpen={editDialogOpen}
            setIsOpen={setEditDialogOpen}
            title={t('dashboardExperienceDetail.editExperienceDetails')}
          >
            <EditExperienceForm
              experienceDetails={experienceDetails}
              refetchExperienceDetails={refetchExperienceDetails}
              closeDialog={() => setEditDialogOpen(false)}
            />
          </ResponsiveDialog>

          <AddAgamiToExperience
            isOpen={addDialogOpen}
            setIsOpen={setAddDialogOpen}
            currentExpId={experienceId as string}
            refetchAgamiList={refetchAgamiList}
          />

          <div className="flex w-full">
            {/* LEFT PART */}
            <div className="grow transition-all duration-[400ms]">
              <Tabs value="cards">
                {/* FIRST ROW */}
                <div className="flex items-start md:items-center justify-between gap-2 mb-2">
                  <div className="flex flex-col md:flex-row md:items-center justify-center gap-4">
                    <GoBack />
                    <Typography
                      variant={screenBreakpoint === 'sm' ? 'h4' : 'lead'}
                      className="underline"
                      data-testid="experience-name"
                    >
                      <span className="inline-block mr-1">
                        <PiPathFill className="h-5 w-5" />
                      </span>
                      {experienceDetails.name}
                      {agamiList?.length > 0 && (
                        <>
                          {' '}
                          <span className="text-base text-gray-600 dark:text-base-gray-500">
                            ({agamiList?.length} {t('dashboardExperienceDetail.agami')})
                          </span>
                        </>
                      )}
                    </Typography>

                    {experienceDetails.is_active && (
                      <Chip
                        value={t('dashboardExperienceDetail.active')}
                        className="inline-block self-start md:self-center"
                        color="green"
                        variant={darkMode ? 'filled' : 'ghost'}
                      />
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-2">
                    <IconButton
                      size={screenBreakpoint === 'sm' ? 'md' : 'sm'}
                      variant="text"
                      color={darkMode ? 'white' : 'gray'}
                      onClick={() => setShowDetails((prev: boolean) => !prev)}
                      data-testid="experience-settings-toggle"
                    >
                      <PiGear className="w-6 md:w-5 h-6 md:h-5 text-primary dark:text-base-gray-500" />
                    </IconButton>
                  </div>
                </div>

                {/* CONTROLS ROW */}
                <div
                  className="flex items-center justify-between gap-2 mb-4"
                  data-testid="experience-controls-row"
                >
                  <div className="flex gap-4">
                    <Button
                      color={'primary' as colors}
                      className="flex justify-center items-center gap-2"
                      size="sm"
                      onClick={() => setAddDialogOpen(true)}
                      data-testid="add-agami-to-exp-btn"
                    >
                      <AiOutlinePlus />
                      {t('dashboardExperienceDetail.addAgami')}
                    </Button>
                    <Button
                      color={'primary' as colors}
                      className="flex justify-center items-center gap-2"
                      size="sm"
                      onClick={() => setEditDialogOpen(true)}
                      data-testid="edit-experience-details-btn"
                    >
                      <AiOutlineEdit />
                      {t('dashboardExperienceDetail.editExperienceDetails')}
                    </Button>
                    <Button
                      size="sm"
                      className="flex justify-center items-center gap-2"
                      onClick={() => navigate(`/experience/${experienceId}`)}
                      data-testid="see-exp-map-btn"
                    >
                      <PiPathFill />
                      {t('dashboardExperienceDetail.seeOnMap')}
                    </Button>
                  </div>
                  <ButtonGroup
                    size="sm"
                    variant={darkMode ? 'filled' : 'outlined'}
                    color={'primary' as colors}
                    className="hidden md:block"
                  >
                    <Button
                      color={'primary' as colors}
                      className={`transition-all duration-[400ms] ${vizMode === 'cards' ? activeSegmentButtonClass : ''}`}
                      onClick={() => setVizMode('cards')}
                      data-testid="viz-mode-cards-btn"
                    >
                      <PiGridFour className="h-5 w-5" />
                    </Button>
                    <Button
                      className={`transition-all duration-[400ms] ${vizMode === 'table' ? activeSegmentButtonClass : ''}`}
                      onClick={() => setVizMode('table')}
                      data-testid="viz-mode-table-btn"
                    >
                      <PiList className="h-5 w-5" />
                    </Button>
                  </ButtonGroup>
                </div>

                {/* FILTERS ROW */}
                <ExperienceAgamiFilters
                  typeFilter={typeFilter}
                  setTypeFilter={setTypeFilter}
                  mediaFilter={mediaFilter}
                  setMediaFilter={setMediaFilter}
                  sortMode={sortMode}
                  setSortMode={setSortMode}
                  darkMode={darkMode}
                />

                {/* CONTENT ROW */}
                <div className="flex gap-4">
                  {/* SPOTS */}
                  <div className="grow transition-all duration-[400ms]">
                    <ExperienceSpotsSections
                      published={published}
                      drafts={draftsForView}
                      draftsTotal={drafts.length}
                      pendingCount={pendingCount}
                      canViewSubmissions={canViewSubmissions}
                      experienceId={experienceId!}
                      canModerate={canModerate}
                      onRejectProposal={startReject}
                      agamiListLoading={agamiListLoading}
                      vizMode={vizMode}
                      removeFromExperience={startRemove}
                      showQrCode={showQrCode}
                    />
                  </div>
                </div>
              </Tabs>
            </div>

            {/* SETTINGS */}
            <ExperienceSettings
              experienceDetails={experienceDetails}
              refetchExperienceDetails={refetchExperienceDetails}
              closeSidebar={() => setShowDetails(false)}
              sidebarIsClosed={!showDetails}
            />
          </div>
        </>
      )}
    </div>
  )
}
